import { Accordion } from 'components/shared/Accordion'
import Box from 'components/shared/Box'
import Info from 'components/shared/Info'
import Link from 'components/shared/Link'
import { RadioTileGroup } from 'components/shared/RadioTileGroup'
import Text from 'components/shared/Text'
import { formatPrice } from 'helpers/helpers'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import { DefaultTheme } from 'styled-components'
import { useAutoScroll } from 'utils/hooks'
import { DateSelector } from './fields/DateSelector/DateSelector'
import { RequirementsWarnings } from './fields/RequirementsWarnings/RequirementsWarnings'
import Summary from './fields/Summary/Summary'
import { TimeSelector } from './fields/TimeSelector/TimeSelector'
import { TimeSelectorRef } from './fields/TimeSelector/TimeSelector.types'
import TreatmentDetailsModal from './fields/TreatmentDetailsModal/TreatmentDetailsModal'
import { VariantSelector } from './fields/VariantSelector/VariantSelector'
import { useSpaCart } from './hooks/useSpaCart'
import { useSpaErrors } from './hooks/useSpaErrors'
import { useSpaSelection } from './hooks/useSpaSelection'
import SpaLoader from './SpaLoader'
import { ContentWrapper, Wrapper } from './styles'
import { GenericTechnicalError } from 'components/global/GenericTechnicalError'

interface SpaEditorProps {
    cartItemId?: string
    theme?: DefaultTheme
    onCancel: () => void
    onSuccess: () => void
}

const SpaEditor = ({
    theme,
    cartItemId,
    onCancel,
    onSuccess,
}: SpaEditorProps) => {
    const { formatMessage } = useIntl()
    const { isMobile } = useResolutions()
    const { calculateScroll } = useAutoScroll()
    const {
        categories,
        isCategoriesError,
        availableDates,
        selection,
        selectTreatment,
        selectVariant,
        selectDate,
        selectTime,
        setInitialSelection,
    } = useSpaSelection()
    const {
        cartItems,
        addItemToCart,
        editItemInCart,
        submitError,
        isSubmitLoading,
        isSubmitSuccess,
        isEditLoading,
        isEditSuccess,
    } = useSpaCart()
    const {
        handleError,
        errorMessage,
        setErrorMessage,
        shouldRefetchHours,
        setShouldRefetchHours,
    } = useSpaErrors()

    const timeSelectRef = useRef<TimeSelectorRef>(null)

    const [treatmentIdForDetails, setTreatmentIdForDetails] = useState<
        string | undefined
    >()
    const [isHoursDataReady, setIsHoursDataReady] = useState(false)

    const isInEditMode = !!cartItemId

    const handleTreatmentChange = (treatmentId: string) => {
        if (!categories) return

        const selectedTreatment = categories
            .flatMap(({ treatments }) => treatments)
            .find(
                ({ spaTreatmentGroupId }) => spaTreatmentGroupId === treatmentId
            )

        if (!selectedTreatment) return

        selectTreatment(treatmentId, selectedTreatment)
        setErrorMessage(undefined)

        const { variants } = selectedTreatment

        if (!variants?.length) return

        const { productSpaTreatmentId } = variants[0]

        // auto select variant if there is only one within selected treatment
        if (variants.length === 1 && productSpaTreatmentId) {
            selectVariant(productSpaTreatmentId, variants[0])
        }
    }

    const handleVariantChange = (variantId: string) => {
        if (!selection.selectedTreatmentData?.variants) return

        const selectedVariant = selection.selectedTreatmentData.variants.find(
            ({ productSpaTreatmentId }) => productSpaTreatmentId === variantId
        )

        if (!selectedVariant) return

        selectVariant(variantId, selectedVariant)
        setErrorMessage(undefined)
    }

    const handleSelectionSubmit = () => {
        const {
            selectedVariantId,
            selectedStartTime,
            selectedEndTime,
            selectedVariantData,
        } = selection

        if (
            !selectedVariantId ||
            !selectedStartTime ||
            !selectedEndTime ||
            !selectedVariantData?.duration
        )
            return

        const dateTimeFormat = 'YYYY-MM-DD HH:mm'

        if (cartItemId) {
            editItemInCart(
                cartItemId,
                selectedVariantId,
                moment(selectedStartTime).format(dateTimeFormat),
                moment(selectedEndTime).format(dateTimeFormat),
                selectedVariantData.duration
            )
        } else
            addItemToCart(
                selectedVariantId,
                moment(selectedStartTime).format(dateTimeFormat),
                moment(selectedEndTime).format(dateTimeFormat),
                selectedVariantData.duration
            )
    }

    const handleSelectDate = (date: string) => {
        selectDate(date)
        setErrorMessage(undefined)
    }

    // TODO: figure out some different solution
    const handleSelectTime = (time: string) => {
        const parsedTime = JSON.parse(time) as {
            startTime: string
            endTime: string
        }
        selectTime(parsedTime.startTime, parsedTime.endTime)
        setErrorMessage(undefined)
    }

    const handleDetails = (id: string) => {
        setTreatmentIdForDetails(id)
    }

    useEffect(() => {
        if (isHoursDataReady && !!errorMessage) {
            selectTime(null, null)
            setShouldRefetchHours(false)
        }
    }, [isHoursDataReady, errorMessage])

    useEffect(() => {
        submitError && handleError(submitError)
    }, [submitError])

    useEffect(() => {
        shouldRefetchHours && timeSelectRef.current?.refetchHours?.()
    }, [shouldRefetchHours])

    useEffect(() => {
        // applies both to adding to and editing items in cart
        if (!isSubmitSuccess && !isEditSuccess) return
        onSuccess()
    }, [isSubmitSuccess, isEditSuccess])

    useEffect(() => {
        if (!cartItemId) return

        const selectedItem = cartItems.find(
            ({ cartItemId: itemId }) => itemId === cartItemId
        )

        if (!selectedItem) return

        const {
            productId,
            detail: { startDate, endDate },
        } = selectedItem

        if (!selection || !categories || !productId) return

        const selectedTreatment = categories
            .map(({ treatments }) => treatments)
            .flat()
            .find(({ variants }) =>
                variants?.some(
                    ({ productSpaTreatmentId }) =>
                        productSpaTreatmentId === productId
                )
            )

        if (!selectedTreatment) return

        const { categoryId, spaTreatmentGroupId } = selectedTreatment

        if (!spaTreatmentGroupId || !selectedTreatment.variants) return

        const selectedVariant = selectedTreatment.variants?.find(
            ({ productSpaTreatmentId }) => productSpaTreatmentId === productId
        )

        if (!selectedVariant) return

        setInitialSelection({
            selectedCategoryId: categoryId || null,
            selectedTreatmentId: spaTreatmentGroupId,
            selectedTreatmentData: selectedTreatment,
            selectedVariantId: productId,
            selectedVariantData: selectedVariant,
            selectedDate: moment(startDate).format('YYYY-MM-DD'),
            selectedStartTime: moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
            selectedEndTime: moment(endDate).format('YYYY-MM-DD HH:mm:ss'),
        })
    }, [cartItemId, categories])

    useEffect(() => {
        if (isInEditMode) return

        calculateScroll({
            trigerringSection: 'spa',
            trigerringAction: 'show',
            hasInstantScroll: true,
        })
    }, [])

    return (
        <Wrapper
            isCategoriesError={isCategoriesError}
            isFullWidth
            theme={theme}
            id="spa-editor"
        >
            {!categories ? (
                <Box isFullWidth>
                    {isCategoriesError ? (
                        <GenericTechnicalError variant="warning" />
                    ) : (
                        <SpaLoader height={[120, 230]} />
                    )}
                </Box>
            ) : (
                <ContentWrapper>
                    <RequirementsWarnings />
                    <Box
                        direction="row"
                        justify="space-between"
                        align={['baseline', 'center']}
                        gap={24}
                        isFullWidth
                    >
                        <Text variant="heading4" color="grayscale_900">
                            <FormattedMessage
                                id="upsells.wellness.choose_category"
                                defaultMessage="Wybierz kategorię i zabieg"
                            />
                        </Text>
                        {!cartItemId && !!cartItems.length && (
                            <Link
                                variant="gray"
                                icon={null}
                                onClick={onCancel}
                                data-testid="spa-button-cancel-edit-top"
                            >
                                <FormattedMessage
                                    id="global.btn.remove"
                                    defaultMessage="Usuń"
                                />
                            </Link>
                        )}
                    </Box>
                    {isMobile && (
                        <Box mt={[16, 0]}>
                            <Info color="info">
                                <FormattedMessage
                                    id="upsells.wellness.info_banner"
                                    defaultMessage="Aby zobaczyć zdjęcia i opis zabiegu, wybierz go z listy."
                                />
                            </Info>
                        </Box>
                    )}
                    {categories &&
                        (!cartItemId || selection.selectedCategoryId) && (
                            <Accordion
                                value={selection.selectedCategoryId}
                                isAutoScrolling
                                data-testid="spa-category"
                            >
                                {categories.map(
                                    ({ id, name, minPrice, treatments }) => (
                                        <Accordion.Item id={id} key={id}>
                                            <Accordion.Header
                                                description={
                                                    minPrice !== undefined
                                                        ? formatMessage(
                                                              {
                                                                  id: 'global.price.label_from_price',
                                                                  defaultMessage:
                                                                      'Od {price} PLN',
                                                              },
                                                              {
                                                                  price: formatPrice(
                                                                      minPrice
                                                                  ),
                                                              }
                                                          )
                                                        : undefined
                                                }
                                            >
                                                <Box
                                                    direction="row"
                                                    gap={4}
                                                    align="baseline"
                                                >
                                                    <Text
                                                        variant="bodyStrong"
                                                        color="grayscale_900"
                                                    >
                                                        {name}
                                                    </Text>
                                                    {!!treatments.length && (
                                                        <Text
                                                            variant="body"
                                                            color="grayscale_800"
                                                        >{`(${treatments.length})`}</Text>
                                                    )}
                                                </Box>
                                            </Accordion.Header>
                                            <Accordion.Content>
                                                <RadioTileGroup
                                                    value={
                                                        selection.selectedTreatmentId
                                                    }
                                                    columns={1}
                                                    isExpandable
                                                    isAutoScrolling
                                                    onChange={
                                                        handleTreatmentChange
                                                    }
                                                    data-testid="spa-treatment"
                                                >
                                                    {treatments.map(
                                                        ({
                                                            spaTreatmentGroupId,
                                                            name,
                                                            description,
                                                            imageUrl,
                                                            prices,
                                                            regularPrices,
                                                            historyPrices,
                                                            times,
                                                            variants,
                                                            badge,
                                                        }) => {
                                                            if (
                                                                // hide treatments with no variants
                                                                !variants?.length ||
                                                                !spaTreatmentGroupId ||
                                                                !name
                                                            )
                                                                return <></>

                                                            return (
                                                                <RadioTileGroup.Item
                                                                    id={
                                                                        spaTreatmentGroupId
                                                                    }
                                                                    key={
                                                                        spaTreatmentGroupId
                                                                    }
                                                                    value={
                                                                        spaTreatmentGroupId
                                                                    }
                                                                    label={name}
                                                                    description={
                                                                        description
                                                                    }
                                                                    price={
                                                                        prices
                                                                    }
                                                                    regularPrice={
                                                                        regularPrices
                                                                    }
                                                                    historyPrice={
                                                                        historyPrices
                                                                    }
                                                                    time={times}
                                                                    image={
                                                                        imageUrl
                                                                    }
                                                                    onCheckDetailsClick={
                                                                        handleDetails
                                                                    }
                                                                    badgeText={
                                                                        badge?.name
                                                                    }
                                                                >
                                                                    <Box
                                                                        gap={[
                                                                            24,
                                                                            40,
                                                                        ]}
                                                                        isFullWidth
                                                                    >
                                                                        {/* show variants selector only if there is more than one variant available */}
                                                                        {variants &&
                                                                            variants.length >
                                                                                1 && (
                                                                                <VariantSelector
                                                                                    value={
                                                                                        selection.selectedVariantId
                                                                                    }
                                                                                    variants={
                                                                                        variants
                                                                                    }
                                                                                    onVariantSelect={
                                                                                        handleVariantChange
                                                                                    }
                                                                                />
                                                                            )}

                                                                        {!!selection.selectedVariantId && (
                                                                            <DateSelector
                                                                                dates={
                                                                                    availableDates
                                                                                }
                                                                                value={
                                                                                    selection.selectedDate
                                                                                }
                                                                                onDateSelect={
                                                                                    handleSelectDate
                                                                                }
                                                                            />
                                                                        )}

                                                                        {!!selection.selectedVariantId &&
                                                                            !!selection.selectedDate && (
                                                                                <>
                                                                                    <TimeSelector
                                                                                        variantId={
                                                                                            selection.selectedVariantId
                                                                                        }
                                                                                        selectedDate={
                                                                                            selection.selectedDate
                                                                                        }
                                                                                        selectedTime={JSON.stringify(
                                                                                            {
                                                                                                startTime:
                                                                                                    selection.selectedStartTime,
                                                                                                endTime:
                                                                                                    selection.selectedEndTime,
                                                                                            }
                                                                                        )}
                                                                                        onDataFetched={
                                                                                            setIsHoursDataReady
                                                                                        }
                                                                                        onTimeSelect={
                                                                                            handleSelectTime
                                                                                        }
                                                                                        ref={
                                                                                            timeSelectRef
                                                                                        }
                                                                                    />
                                                                                </>
                                                                            )}

                                                                        {(!!selection.selectedStartTime ||
                                                                            !!errorMessage) &&
                                                                            isHoursDataReady && (
                                                                                <Summary
                                                                                    price={
                                                                                        selection
                                                                                            .selectedVariantData
                                                                                            ?.price
                                                                                    }
                                                                                    errorMessage={
                                                                                        errorMessage
                                                                                    }
                                                                                    isDisabled={
                                                                                        !!errorMessage ||
                                                                                        !selection.selectedStartTime
                                                                                    }
                                                                                    isLoading={
                                                                                        isSubmitLoading ||
                                                                                        isEditLoading
                                                                                    }
                                                                                    onCancel={
                                                                                        onCancel
                                                                                    }
                                                                                    onSubmit={
                                                                                        handleSelectionSubmit
                                                                                    }
                                                                                />
                                                                            )}
                                                                    </Box>
                                                                </RadioTileGroup.Item>
                                                            )
                                                        }
                                                    )}
                                                </RadioTileGroup>
                                            </Accordion.Content>
                                        </Accordion.Item>
                                    )
                                )}
                            </Accordion>
                        )}
                    {!!treatmentIdForDetails && (
                        <TreatmentDetailsModal
                            treatmentId={treatmentIdForDetails}
                            isOpened={!!treatmentIdForDetails}
                            closeModal={() =>
                                setTreatmentIdForDetails(undefined)
                            }
                        />
                    )}
                </ContentWrapper>
            )}
        </Wrapper>
    )
}
export default SpaEditor
